import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BaseLoginComponent } from 'src/app/shared/components/base-login/base-login.component';
import { errorDescriptionsMap } from 'src/app/shared/utils';
import { EMAIL_FORMAT_RE } from '../healthpicks.constants';
import {
  applyBulletMaskToInput,
  derivePasswordFromMaskedInput,
  HP_PW_BULLET,
} from '../masked-bullet-input.util';

@Component({
  selector: 'app-healthpicks-login',
  templateUrl: './healthpicks-login.component.html',
  styleUrls: ['../style/global.css', '../style/hp-shared.css', './healthpicks-login.component.css'],
})
export class HealthpicksLoginComponent
  extends BaseLoginComponent
  implements OnInit, AfterViewInit
{
  /** Native `type="password"` bullets ≠ Figma ● (U+25CF); mirror layer shows design glyphs. */
  passwordFieldFocused = false;
  isPasswordVisible = false;

  /** Client-side field validation error for password (required). Separate from server auth errors. */
  passwordFieldError = '';

  @ViewChild('passwordInput', { static: false })
  passwordInput?: ElementRef<HTMLInputElement>;

  get passwordMirrorText(): string {
    const v = this.password ?? '';
    if (v.length > 0) {
      return HP_PW_BULLET.repeat(v.length);
    }
    return this.passwordFieldFocused ? '' : HP_PW_BULLET.repeat(7);
  }

  ngAfterViewInit(): void {
    // Initial sync after view renders: set bullet mask if password already populated
    this.syncPasswordBulletDom(false);
  }

  private syncPasswordBulletDom(force: boolean): void {
    const el = this.passwordInput?.nativeElement;
    if (!el) {
      return;
    }
    if (!force && document.activeElement === el) {
      return;
    }
    const pwd = this.password ?? '';
    if (this.isPasswordVisible) {
      if (el.value !== pwd) {
        el.value = pwd;
      }
      return;
    }
    if (el.value === HP_PW_BULLET.repeat(pwd.length)) {
      return;
    }
    applyBulletMaskToInput(el, pwd);
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
    this.syncPasswordBulletDom(true);
  }

  onPasswordBulletInput(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    if (this.isPasswordVisible) {
      this.password = el.value;
      return;
    }
    const next = derivePasswordFromMaskedInput(el.value, this.password ?? '');
    this.password = next;
    applyBulletMaskToInput(el, next);
  }

  onPasswordFocus(): void {
    this.passwordFieldFocused = true;
    this.syncPasswordBulletDom(true);
  }

  onPasswordBlur(): void {
    this.passwordFieldFocused = false;
    this.syncPasswordBulletDom(true);
  }

  async init(): Promise<void> {}

  isLoginInfoInput(): boolean {
    // Clear server error from previous attempt
    this.errorMessages.login.password = '';

    // Validate both fields to show all errors at once
    const emailTrimmed = this.email.trim();
    let valid = true;

    if (!emailTrimmed) {
      this.errorMessages.login.email = 'メールアドレスは必須項目です';
      valid = false;
    } else if (!EMAIL_FORMAT_RE.test(emailTrimmed)) {
      this.errorMessages.login.email = 'メールアドレスまたはパスワードを確認してください。';
      valid = false;
    } else {
      this.errorMessages.login.email = '';
      this.email = emailTrimmed;
    }

    if (!this.password) {
      this.passwordFieldError = 'パスワードは必須項目です';
      valid = false;
    } else {
      this.passwordFieldError = '';
    }

    return valid;
  }

  get isMedicalUser() {
    return this.role !== 'patient';
  }

  get isOem() {
    return false;
  }

  get isRegisterResendable(): boolean {
    return true;
  }

  get isRegistable() {
    return false;
  }

  protected get errorDescripitionsMap() {
    return {
      ...errorDescriptionsMap,
      'Wrong email or password.': 'メールアドレスまたはパスワードを確認してください。',
      "Your account has been blocked after multiple consecutive login attempts. We've sent you an email with instructions on how to unblock it.": 'このアカウントはロックまたは無効化されています。管理者にお問い合わせください。',
      "Your account has been blocked after multiple consecutive login attempts. We've sent you a notification via your preferred contact method with instructions on how to unblock it.": 'このアカウントはロックまたは無効化されています。管理者にお問い合わせください。'
    };
  }
}
